import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { turndropType } from "app/data/static-load/model";
import { ReactComponent as DotIcon } from "images/dots.svg";
import XGSSelect, { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import "./routeBuilder.scss";

export type RouteBuilderDrop = {
  id: string;
  dropType: string;
  terminalNumber?: string;
};

type RouteBuilderProps = {
  drops: RouteBuilderDrop[];
  terminalOptions: XGSSelectOption[];
  onDropsChange: (drops: RouteBuilderDrop[]) => void;
};

const TYPE_BADGE_CLASS: Record<string, string> = {
  O: "origin",
  F: "destination",
  S: "intermediate",
};

const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getTypeLabel = (dropType?: string) =>
  turndropType.find((item) => item.value === dropType)?.label ?? "-";

const RouteBuilderHeader: React.FC = () => (
  <div className="route-builder__header">
    <div className="route-builder__header-label">Route Structure</div>
    <div className="route-builder__header-subtitle">
      Select Origin &amp; Destination · Arrival times and mileage are configured in Edit Lane
    </div>
  </div>
);

type RouteBuilderRowProps = {
  drop: RouteBuilderDrop;
  terminalOptions: XGSSelectOption[];
  onTerminalChange: (dropId: string, terminalNumber: string) => void;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: React.HTMLAttributes<HTMLDivElement>;
  style?: React.CSSProperties;
};

const RouteBuilderRow: React.FC<RouteBuilderRowProps> = ({
  drop,
  terminalOptions,
  onTerminalChange,
  isDragging,
  dragHandleProps,
  innerRef,
  draggableProps,
  style,
}) => {
  const badgeClass = TYPE_BADGE_CLASS[drop.dropType ?? ""] ?? "origin";
  const typeLabel = getTypeLabel(drop.dropType);
  const selectedTerminal = terminalOptions.find(
    (option) => option.value.toString() === drop.terminalNumber?.toString()
  ) ?? null;

  return (
    <div
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      style={style}
      className={[
        "route-builder__row",
        isDragging ? "route-builder__row--dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="route-builder__type-cell">
        <span className="route-builder__drag-handle">
          <DotIcon />
        </span>
        <span className={`route-builder__type-badge route-builder__type-badge--${badgeClass}`}>
          {typeLabel}
        </span>
      </div>
      <div className="route-builder__terminal-cell">
        <XGSSelect
          className="route-builder__terminal-select"
          options={terminalOptions}
          value={selectedTerminal}
          placeholder="Select Terminal"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 1100 }),
          }}
          onValueChange={(option) => {
            onTerminalChange(drop.id, option?.value?.toString() ?? "");
          }}
        />
      </div>
    </div>
  );
};

const RouteBuilder: React.FC<RouteBuilderProps> = ({
  drops,
  terminalOptions,
  onDropsChange,
}) => {
  const [orderedDrops, setOrderedDrops] = useState<RouteBuilderDrop[]>(drops);

  useEffect(() => {
    setOrderedDrops(drops);
  }, [drops]);

  const handleTerminalChange = (dropId: string, terminalNumber: string) => {
    const updated = orderedDrops.map((drop) =>
      drop.id === dropId ? { ...drop, terminalNumber } : drop
    );
    setOrderedDrops(updated);
    onDropsChange(updated);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = reorder(orderedDrops, result.source.index, result.destination.index);
    setOrderedDrops(reordered);
    onDropsChange(reordered);
  };

  return (
    <div className="route-builder">
      <RouteBuilderHeader />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="route-builder-drops">
          {(provided) => (
            <div
              className="route-builder__body"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {orderedDrops.map((drop, index) => (
                <Draggable
                  key={drop.id}
                  draggableId={`route-builder-${drop.id}`}
                  index={index}
                >
                  {(draggableProvided, snapshot) => (
                    <RouteBuilderRow
                      drop={drop}
                      terminalOptions={terminalOptions}
                      onTerminalChange={handleTerminalChange}
                      isDragging={snapshot.isDragging}
                      innerRef={draggableProvided.innerRef}
                      draggableProps={draggableProvided.draggableProps}
                      dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                      style={draggableProvided.draggableProps.style}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default RouteBuilder;
